// hooks/useFeedItemDetail.js
import { useState, useEffect } from 'react';

const apiKey = process.env.REACT_APP_API_KEY;

const TABLES_NEEDING_MEMBERS = [
  'congressional_bills',
  'congressional_bills_enrolled',
  'public_and_private_laws',
  'all_member_votes'
];

const BILL_TABLES = ['congressional_bills', 'congressional_bills_enrolled', 'public_and_private_laws'];

// ─── Bills by GovInfo package id ──────────────────────────────────────────────
//
// congressional_bills stores one row per printed version of a bill, keyed by
// its GovInfo package id: BILLS-119hr10473ih is H.R. 10473 as introduced,
// ...rh as reported, ...eh as passed the House, ...enr as sent to the
// President.
//
// api2 has one row per bill and no version, so its links arrive without the
// letters: BILLS-119hr10473. For those, the versions a bill of that kind
// usually has are asked for at once and the furthest one found is shown --
// it carries the fullest action history. A Senate simple resolution agreed to
// on the day it was submitted is only ever printed as "ats", which is why the
// agreed-to versions are on the lists.
//
// A link that already names its version (the api1 lists' own links) is looked
// up exactly, as before.
const PACKAGE_ID = /^BILLS-(\d+)(hconres|sconres|hjres|sjres|hres|sres|hr|s)(\d+)([a-z]*)$/i;

// Furthest first: the first one found is the one shown.
const HOUSE_VERSIONS = ['enr', 'eas', 'eh', 'rfs', 'rh', 'pch', 'ath', 'ih'];
const SENATE_VERSIONS = ['enr', 'eah', 'es', 'rfh', 'rs', 'pcs', 'ats', 'is'];

const versionsFor = (type) =>
  type.toLowerCase().startsWith('s') ? SENATE_VERSIONS : HOUSE_VERSIONS;

// ─── Vote links ───────────────────────────────────────────────────────────────
//
// Every vote page reads all_member_votes by roll-call number, api1 only. The
// member page links House roll calls as 314?date=2026-09-16; the date keeps a
// roll call apart from the same number in the other session.
//
// A link in api2's id form (house-2026-314, from before the member page linked
// by number) is read the same way: the number is always the last part, and
// the link's date narrows it.
const API2_VOTE_ID = /^(house|senate)(?:-\d+){1,2}-(\d+)$/i;

const rollCallNumber = (id) => {
  const match = API2_VOTE_ID.exec(String(id));
  return match ? match[2] : id;
};

// ─── Members ──────────────────────────────────────────────────────────────────

// Module-level cache — persists for the app session, shared across all detail pages
const membersCache = { data: null, promise: null };

const fetchMembers = () => {
  if (membersCache.data) return Promise.resolve(membersCache.data);
  if (membersCache.promise) return membersCache.promise;

  membersCache.promise = fetch(
    'https://api.govglance.org/members/?current_member=true&order_by=state&limit=541&skip=0',
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      }
    }
  )
    .then(res => {
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      return res.json();
    })
    .then(data => {
      membersCache.data = data;
      membersCache.promise = null;
      return data;
    })
    .catch(err => {
      membersCache.promise = null; // allow retry on failure
      throw err;
    });

  return membersCache.promise;
};

// ─── Items ────────────────────────────────────────────────────────────────────

const fetchRows = async (schema, table, filterColumn, filterString, limit) => {
  const url =
    `https://api.govglance.org/posts/recent?limit=${limit}&skip=0&schema=${schema}&table=${table}` +
    `&order_by=created_at&filter_column=${filterColumn}&filter_string=${encodeURIComponent(filterString)}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    }
  });

  if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
  const result = await res.json();
  return Array.isArray(result) ? result : [];
};

const fetchBillByPackage = async (schema, table, id) => {
  const match = PACKAGE_ID.exec(id);
  const [, congress, type, number, version] = match;

  if (version) {
    return fetchRows(schema, table, 'package_id', id, 1);
  }

  const base = `BILLS-${congress}${type.toLowerCase()}${Number(number)}`;
  const candidates = versionsFor(type).map((v) => `${base}${v}`);
  const results = await Promise.all(
    candidates.map((packageId) => fetchRows(schema, table, 'package_id', packageId, 1).catch(() => []))
  );
  return results.find((rows) => rows.length) || [];
};

// Roll-call numbers restart every session, so a number alone can match a
// roll call from each year. When the link carries the vote's date, only that
// day's rows are kept -- and the page no longer mixes two roll calls'
// members into one list. Without a date the rows are returned as before.
const fetchVoteRows = async (schema, table, id, date) => {
  const rows = await fetchRows(schema, table, 'roll_call_number', id, date ? 2000 : 551);
  if (!date) return rows;
  const onDay = rows.filter((row) => String(row.created_at || '').startsWith(date));
  return onDay.length ? onDay : rows;
};

const fetchItem = async (schema, table, id, { date } = {}) => {
  let result;

  if (table === 'all_member_votes') {
    result = await fetchVoteRows(schema, table, rollCallNumber(id), date);
  } else if (BILL_TABLES.includes(table) && PACKAGE_ID.test(id)) {
    result = await fetchBillByPackage(schema, table, id);
  } else {
    result = await fetchRows(schema, table, 'id', id, 1);
  }

  if (!result.length) {
    // A bill api2 knows about can be days ahead of GovInfo publishing its
    // text, which is when it first appears in this table.
    if (BILL_TABLES.includes(table) && PACKAGE_ID.test(id)) {
      throw new Error('This bill\u2019s text has not been published by GovInfo yet. Check back in a few days.');
    }
    // Named by the roll-call number actually asked for, so it is clear which
    // lookup came back empty.
    if (table === 'all_member_votes') {
      throw new Error(`Roll call ${rollCallNumber(id)} is not in all_member_votes yet.`);
    }
    throw new Error(`Item with ID ${id} not found`);
  }
  return result;
};

export const useFeedItemDetail = (schema, table, id, { date } = {}) => {
  const [item, setItem] = useState(null);
  const [allVotes, setAllVotes] = useState([]);
  const [membersData, setMembersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!schema || !table || !id) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const needsMembers = TABLES_NEEDING_MEMBERS.includes(table);

    Promise.all([
      fetchItem(schema, table, id, { date }),
      needsMembers ? fetchMembers() : Promise.resolve([])
    ])
      .then(([itemResult, membersResult]) => {
        if (cancelled) return;
        setItem(itemResult[0]);
        if (table === 'all_member_votes') setAllVotes(itemResult);
        setMembersData(membersResult);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.message);
        console.error('Error fetching item detail:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [schema, table, id, date]);

  return { item, allVotes, membersData, loading, error };
};